// services/admin/routeService.js
import { apiClient } from './apiClient';

export const routeService = {
  /**
   * Создание маршрута
   * @param {Object} routeData - Данные маршрута
   * @returns {Promise<Route>}
   */
  async createRoute(routeData) {
    return apiClient.post('/routes', routeData);
  },

  /**
   * Получение основной фотографии маршрута
   * @param {number} routeId - ID маршрута
   * @returns {Promise<Photo>}
   */
  async getMainPhoto(routeId) {
    return apiClient.get(`/routes/${routeId}/main-photo`);
  },

  /**
   * Загрузка основного фото для маршрута
   * @param {number} routeId - ID маршрута
   * @param {File} photo - Файл фотографии
   * @returns {Promise<Photo>}
   */
  async uploadMainPhoto(routeId, photo) {
    const formData = new FormData();
    formData.append('photo', photo);
    
    // Используем postFormData для загрузки файлов
    return apiClient.postFormData(`/routes/${routeId}/main-photo`, formData);
  },

  /**
   * Загрузка фото для точки
   * @param {number} pointId - ID точки
   * @param {File[]} photos - Массив файлов фотографий
   * @returns {Promise<Object>}
   */
  async uploadPointPhotos(pointId, photos) {
    const formData = new FormData();
    photos.forEach(photo => {
      formData.append('photos', photo);
    });

    const url = `${apiClient.baseUrl}/points/${pointId}/photos`;
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Ошибка при загрузке фото точки');
    }

    const result = await response.json();
    return result?.data !== undefined ? result.data : result;
  },

  /**
   * Получение всех маршрутов
   * @returns {Promise<Route[]>}
   */
  async getAllRoutes() {
    return apiClient.get('/routes');
  },

  /**
   * Получение маршрута по ID
   * @param {number} id - ID маршрута
   * @returns {Promise<Route>}
   */
  async getRouteById(id) {
    return apiClient.get(`/routes/${id}`);
  },

  /**
   * Обновление маршрута
   * @param {number} id - ID маршрута
   * @param {Object} routeData - Данные для обновления
   * @returns {Promise<Route>}
   */
  async updateRoute(id, routeData) {
    return apiClient.put(`/routes/${id}`, routeData);
  },

  /**
   * Частичное обновление маршрута (PATCH)
   * @param {number} id - ID маршрута
   * @param {Object} routeData - Данные для обновления
   * @returns {Promise<Route>}
   */
  async patchRoute(id, routeData) {
    return apiClient.patch(`/routes/${id}`, routeData);
  },

  /**
   * Удаление маршрута
   * @param {number} id - ID маршрута
   * @returns {Promise<{message: string}>}
   */
  async deleteRoute(id) {
    return apiClient.delete(`/routes/${id}`);
  },

  /**
   * Получение маршрутов по ID категории
   * @param {number} categoryId - ID категории
   * @returns {Promise<Route[]>}
   */
  async getRoutesByCategoryId(categoryId) {
    return apiClient.get(`/categories/${categoryId}/routes`);
  },

  /**
   * Получение всех категорий маршрута
   * @param {number} routeId - ID маршрута
   * @returns {Promise<Category[]>}
   */
  async getRouteCategories(routeId) {
    return apiClient.get(`/routes/${routeId}/categories`);
  },
  
  /**
   * Получение маршрутов по ID тега
   * @param {number} tagId - ID тега
   * @returns {Promise<Route[]>}
   */
  async getRoutesByTagId(tagId) {
    return apiClient.get(`/tags/${tagId}/routes`);
  },

  /**
   * Получение всех тегов маршрута
   * @param {number} routeId
   * @returns {Promise<Tags[]>}
   */
  async getTagsByRouteId(routeId) {
    return apiClient.get(`/routes/${routeId}/tags`);
  },

  /**
   * Поиск маршрута
   * @param {Object} params - Параметры поиска
   * @returns {Promise<Route[]>}
   */
  async searchRoutes(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.name) queryParams.append('name', params.name);
    if (params.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params.city) queryParams.append('city', params.city);
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/routes/search?${queryString}` : '/routes';
    
    return apiClient.get(endpoint);
  }
};